import * as React from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useNotification } from "../../context/notification-provider";
import LoadingScreen from "../progress/loading-screen";
import FilterTypeForm, {
  FilterTypeFormState,
  FilterTypeFormValues,
  FormFieldValue,
  validateFilterType,
} from "./filter-type-form";
import useFilterShapes from "./use-filter-shapes";
import { createFilterType } from "./service";
import {
  FilterDefaultVisibility,
  FilterSelectionMode,
  FilterShape,
  FilterSource,
} from "./types";

const FilterTypeCreateDialog = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { data: shapes, error: loadingError, isLoading } = useFilterShapes();
  const { showNotification } = useNotification();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const [formState, setFormState] = React.useState<FilterTypeFormState>({
    values: {
      selectionMode: FilterSelectionMode.Multi,
      defaultVisibility: FilterDefaultVisibility.VisibleToAll,
      sortOrder: 0,
      isEnabled: true,
      isDropdown: false,
    },
    errors: {},
  });
  const formValues = formState.values;

  const handleFieldChange = React.useCallback(
    (name: keyof FilterTypeFormValues, value: FormFieldValue) => {
      setFormState((previousState) => ({
        ...previousState,
        values: { ...previousState.values, [name]: value },
      }));
    },
    [],
  );

  const handleSubmit = React.useCallback(async () => {
    const { issues } = validateFilterType(formValues, false);

    if (issues.length > 0) {
      setFormState((previousState) => ({
        ...previousState,
        errors: Object.fromEntries(
          issues.map((issue) => [issue.path[0], issue.message]),
        ),
      }));
      return;
    }

    setFormState((previousState) => ({ ...previousState, errors: {} }));
    setIsSubmitting(true);

    try {
      await createFilterType({
        code: formValues.code ?? "",
        label: formValues.label ?? "",
        shapeId: formValues.shapeId as FilterShape,
        source: formValues.source as FilterSource,
        selectionMode: formValues.selectionMode ?? FilterSelectionMode.Multi,
        defaultVisibility:
          formValues.defaultVisibility ?? FilterDefaultVisibility.VisibleToAll,
        sortOrder: formValues.sortOrder ?? 0,
        targetField: formValues.targetField || null,
        targetColumn: formValues.targetColumn || null,
        isEnabled: formValues.isEnabled ?? true,
        isDropdown: formValues.isDropdown ?? false,
      });

      showNotification({
        message: "Filter type created successfully.",
        severity: "success",
        duration: 3000,
      });

      onClose();
    } catch (createError) {
      showNotification({
        message: `Failed to create filter type. Reason: ${(createError as Error).message}`,
        severity: "error",
        duration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [formValues, showNotification, onClose]);

  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="md" fullWidth>
      <DialogTitle sx={{ borderBottom: 2, mb: 2, borderColor: "divider" }}>
        Create Filter Type
      </DialogTitle>
      {isLoading ? (
        <LoadingScreen />
      ) : (
        <DialogContent sx={{ pb: 0 }}>
          {loadingError ? (
            <Alert severity="error">Unable to load filter shapes.</Alert>
          ) : (
            <FilterTypeForm
              formState={formState}
              shapes={shapes || []}
              isEdit={false}
              onFieldChange={handleFieldChange}
              onSubmit={handleSubmit}
            />
          )}
        </DialogContent>
      )}
      <DialogActions>
        <Button onClick={() => onClose()}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={isSubmitting}
        >
          Create filter type
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilterTypeCreateDialog;
